import React from 'react';
import { Quill } from 'react-quill';

const BubbleTheme = Quill.import('themes/bubble');

export const setupColorPicker = (quillRef: React.RefObject<any>) => {
  // Both should be present in toolbar element
  const colorButton = document.getElementById(
    'custom-color-btn',
  ) as HTMLButtonElement;
  const colorInput = document.getElementById(
    'custom-color-input',
  ) as HTMLInputElement;

  if (colorButton && colorInput) {
    // Remove existing event listeners to prevent duplicates
    colorButton.onclick = null;
    colorInput.onchange = null;

    colorButton.onclick = () => {
      const quill = quillRef.current;
      if (quill) {
        const range = quill.getSelection();
        if (range && range.length > 0) {
          colorInput.dataset.selection = JSON.stringify(range);
        }
      }
      colorInput.click();
    };

    colorInput.onchange = () => {
      const color = colorInput.value;
      const quill = quillRef.current;

      if (quill) {
        const storedSelection = colorInput.dataset.selection;

        if (storedSelection) {
          try {
            const range = JSON.parse(storedSelection);
            quill.setSelection(range);
          } catch (e) {
            // If restoration fails, use current selection
          }
        }

        // Apply color format with the selected color
        quill.format('color', color);

        // Force a change event to ensure the change is detected
        quill.focus();

        const delta = quill.getContents();
        const html = quill.root.innerHTML;

        const changeEvent = new Event('input', { bubbles: true });
        quill.root.dispatchEvent(changeEvent);

        if (quill.onChange) {
          quill.onChange(html, delta, 'api', quill);
        }

        setTimeout(() => {
          const currentRange = quill.getSelection();
          if (currentRange) {
            quill.setSelection(currentRange);
          }
        }, 10);
      }

      colorInput.value = '#000000';
      delete colorInput.dataset.selection;
    };
  }
};

export const setupColorPickerForBubble = (
  quillRef: React.RefObject<any>,
  colorButtonId = 'custom-color-btn',
  colorInputId = 'custom-color-input',
) => {
  const setupColorPickerInTooltip = () => {
    const quill = quillRef.current;
    if (!quill) return;

    const editorRoot = quill.root;
    const editorContainer = editorRoot.closest('.ql-container');

    let tooltip: Element | null = null;

    if (editorContainer) {
      const parent = editorContainer.parentElement;
      if (parent) {
        tooltip = parent.querySelector('.ql-tooltip');
      }
    }

    if (!tooltip) {
      const allTooltips = document.querySelectorAll('.ql-bubble .ql-tooltip');
      for (const t of allTooltips) {
        const tooltipEl = t as HTMLElement;
        if (
          tooltipEl.offsetParent !== null ||
          tooltipEl.style.display !== 'none'
        ) {
          tooltip = t;
          break;
        }
      }
      if (!tooltip && allTooltips.length > 0) {
        tooltip = allTooltips[0];
      }
    }

    if (tooltip) {
      let colorButton = tooltip.querySelector(
        `#${colorButtonId}`,
      ) as HTMLButtonElement;
      let colorInput = tooltip.querySelector(
        `#${colorInputId}`,
      ) as HTMLInputElement;

      if (!colorInput) {
        colorInput = document.getElementById(colorInputId) as HTMLInputElement;
      }

      if (!colorButton) {
        colorButton = tooltip.querySelector(
          '#custom-color-btn',
        ) as HTMLButtonElement;
      }
      if (!colorInput) {
        colorInput = document.getElementById(
          'custom-color-input',
        ) as HTMLInputElement;
      }

      if (colorButton && colorInput && !colorButton.dataset.listenerAttached) {
        colorButton.dataset.listenerAttached = 'true';

        colorButton.onclick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          const quill = quillRef.current;
          if (quill) {
            const range = quill.getSelection();
            if (range && range.length > 0) {
              colorInput.dataset.selection = JSON.stringify(range);
            }
          }
          colorInput.click();
        };

        colorInput.onchange = () => {
          const color = colorInput.value;
          const quill = quillRef.current;

          if (quill) {
            const storedSelection = colorInput.dataset.selection;

            if (storedSelection) {
              try {
                const range = JSON.parse(storedSelection);
                quill.setSelection(range);
              } catch (e) {
                // If restoration fails, use current selection
              }
            }

            quill.format('color', color);

            const delta = quill.getContents();
            const html = quill.root.innerHTML;

            const changeEvent = new Event('input', { bubbles: true });
            quill.root.dispatchEvent(changeEvent);

            if (quill.onChange) {
              quill.onChange(html, delta, 'api', quill);
            }

            setTimeout(() => {
              const currentRange = quill.getSelection();
              if (currentRange) {
                quill.setSelection(currentRange);
              }
            }, 10);
          }

          colorInput.value = '#000000';
          delete colorInput.dataset.selection;
        };
      }
    }
  };

  const observer = new MutationObserver(() => {
    setupColorPickerInTooltip();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  setupColorPickerInTooltip();
  const interval = setInterval(setupColorPickerInTooltip, 200);

  return () => {
    observer.disconnect();
    clearInterval(interval);
  };
};

// Fix for link placeholder in bubble theme
class BubbleThemeLinkPlaceholderFix extends BubbleTheme {
  extendToolbar(toolbar: any) {
    super.extendToolbar(toolbar);
    this.tooltip.textbox.dataset.link = 'https://cyberpilot.io/';
  }
}
Quill.register('themes/bubble', BubbleThemeLinkPlaceholderFix, true);
